import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import { userId_local_storage_key } from "../../../../helpers/common";
import { getCustomerDetails } from "../../../../actions/customer";

const EXEMPT_PATH_PREFIXES = ["/compulsary/profile", "/thank-you"];

const CANDIDATE_REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "gender",
  "designation",
  "location",
  "totalWorkExperience",
  "resumeFile",
  "currentSalaryStr",
  "expectedSalaryStr",
  "jobInduestry",
];

const getFreshProfileData = (profile) => {
  const data = profile?.data?.data ?? profile?.data;
  return data && typeof data === "object" ? data : null;
};

const getCustomerType = (profileData) => {
  const rawType = profileData?.customerType ?? profileData?.customer_type;
  if (rawType === undefined || rawType === null || rawType === "") return null;
  const customerType = Number(rawType);
  return Number.isFinite(customerType) ? customerType : null;
};

// Role saved at login (webvi_userInfo holds the login response).
// Fast path only: fresh data replaces it as soon as it arrives.
const getStoredCustomerType = () => {
  try {
    const raw = localStorage.getItem("webvi_userInfo");
    if (!raw) return null;
    return getCustomerType(JSON.parse(raw)?.data);
  } catch (error) {
    return null;
  }
};

const getAdditionalFields = (profileData) => {
  const additionalFields =
    profileData?.additionalFields ?? profileData?.additional_fields;
  if (typeof additionalFields === "string" && additionalFields.trim()) {
    try {
      return JSON.parse(additionalFields);
    } catch (error) {
      return {};
    }
  }
  return additionalFields ?? {};
};

const getField = (profileData, key) =>
  profileData?.[key] ?? getAdditionalFields(profileData)?.[key] ?? "";

const isFieldPresent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const isCandidateComplete = (profileData) => {
  if (!profileData) return false;
  if (profileData.isProfileUpdated === true) return true;

  return CANDIDATE_REQUIRED_FIELDS.every((key) =>
    isFieldPresent(getField(profileData, key)),
  );
};

const ProfileCompletionGuard = ({ children }) => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.customer?.profile);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchStartedRef = useRef(false);
  const completedRef = useRef(false);
  const [fetchSettled, setFetchSettled] = useState(false);

  const storedUserId = localStorage.getItem(userId_local_storage_key);
  const freshProfileData = getFreshProfileData(profile);
  const freshType = getCustomerType(freshProfileData);

  // Fresh data wins; stored login role is only a fast path before it arrives.
  const knownType = freshType ?? getStoredCustomerType();

  // Employers are never blocked or redirected by this guard.
  const isEmployer = knownType === 1;
  const isCandidate = freshType === 0;

  const isLoggedIn = Boolean(
    profile?.id || profile?.email || freshProfileData?.email || storedUserId,
  );
  const userId = profile?.id || freshProfileData?.id || storedUserId;

  // 🎯 FIX 1: fetch the fresh profile for EVERYONE when it's missing.
  // Employers are not blocked, but the app (e.g. the Footer) needs
  // profile.data to load their jobs, companies and applications.
  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    if (freshProfileData) return;
    if (fetchStartedRef.current) return;

    fetchStartedRef.current = true;
    Promise.resolve(dispatch(getCustomerDetails(userId))).finally(() =>
      setFetchSettled(true),
    );
  }, [isLoggedIn, userId, freshProfileData, dispatch]);

  const candidateComplete = isCandidateComplete(freshProfileData);

  // 🎯 FIX 2: once seen complete, a candidate stays complete while this page
  // is mounted, so a profile update can't kick them back to the form.
  if (candidateComplete) completedRef.current = true;
  const stayComplete = candidateComplete || completedRef.current;

  const isExempt = EXEMPT_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );

  useEffect(() => {
    if (isExempt || !isLoggedIn || !isCandidate || stayComplete) return;

    CustomSnackbar.error(
      "Please complete your basic profile before accessing this page.",
    );
    navigate("/compulsary/profile", {
      replace: true,
      state: { from: location.pathname },
    });
  }, [
    location.pathname,
    navigate,
    isLoggedIn,
    isCandidate,
    stayComplete,
    isExempt,
  ]);

  if (isExempt || isEmployer) return children;

  // Role not known yet: wait for the one fetch.
  if (isLoggedIn && !freshProfileData && !fetchSettled) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Loading your profile...</p>
      </div>
    );
  }

  // Incomplete candidate: render nothing while the redirect happens.
  if (isLoggedIn && isCandidate && !stayComplete) return null;

  return children;
};

export default ProfileCompletionGuard;